import {
    sendCustomEvent, 
    throwCustomError,
    abortableFetch
} from '../../assets/js/utils.js';
import { toggleHinweis } from '../../modules/hinweis/hinweis.js';

class AudioCustom extends HTMLElement {
    constructor() {
        super();
        this.currentTrack = 0;
        this.keepPlaying = true;
        this.audio = this.querySelector('.audio');
        this.controls = this.querySelectorAll('[data-control]:not([data-control="close"])');
        this.playButton = this.querySelector('[data-control="play"]');
        this.pauseButton = this.querySelector('[data-control="pause"]');
        this.nextButton = this.querySelector('[data-control="next"]');
    }

    static get observedAttributes() {
        return ['data-state'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'data-state') {
            this.stateHandler(newValue);
        }
    }

    stateHandler(state) {
        // Initialer Aufruf: Track laden, Events registrieren
        if (state === 'active' && this.audio.readyState === 0) {
            this.changeTrack();
            this.addEvents();
        }
    }

    addEvents() {
        this.audio.addEventListener('canplay', () => {
            this.play();
        })

        this.audio.addEventListener('ended', () => {
            this.next();
        })

        for (const control of this.controls) {
            control.addEventListener('click', (event) => {
                this.controlHandler(event);
            })
        }

        document.addEventListener('audio-custom-pause', () => {
            this.pause();
        })
    }

    async controlHandler(event) {
        const controlType = event.currentTarget.dataset['control'];
        switch (controlType) {
            case 'play':
                this.keepPlaying = true;
                this.play();
                break;
            case 'pause':
                this.pause();
                break;
            case 'next':    
                this.next();
                break;
            case 'close':
                this.closeAudioplayer();
                break;
            default:
                break;
        }
    }

    async play() {
        if (this.isActive && this.isPaused && this.keepPlaying) {
            try {
                await this.audio.play();
                this.togglePlayPause(true);
            } catch (error) {
                this.errorHandler(error);
            }
        }
    }

    pause() {
        this.audio.pause();
        this.togglePlayPause();
    }

    async next() {
        this.keepPlaying = true;
        if (this.isLastTrack) this.lastTrackHandler();
        this.updateCurrentTrack();
        await this.changeTrack();
    }

    lastTrackHandler() {
        this.pause();
        this.keepPlaying = false;
        if (this.totalCds > 1) {
            sendCustomEvent('audio-custom-cd-ended', { 
                cd: this.cd
            });
        }
    }

    updateCurrentTrack() {
        this.currentTrack = this.isLastTrack ? 0 : this.currentTrack + 1;
    }

    async changeTrack() {
        const { cd, title, track, src } = this.getAudioData(this.currentTrack);
        await this.updateAudioElement(src);
        this.updateTrackTitle(cd, track, title);
    }

    getAudioData(number) {
        const trackData = this.querySelector(`.tracklist [data-track="${number}"]`);
        return trackData.dataset;
    }

    async updateAudioElement(newSrc) {
        this.enableControls();
        try { 
            if (newSrc.length === 0) throwCustomError('Empty Source', 'EmptySourceError');
            const response = await abortableFetch(newSrc, { timeout: 4000 });
            if (response.status !== 200) throw new Error(response.statusText);
            this.audio.src = newSrc;
            this.audio.load();
        } catch (error) {
            if (!this.isPaused) this.pause();
            this.errorHandler(error);
        }
    }

    updateTrackTitle(cd, track, title) {
        const currentCd = this.totalCds === 1 ? '' : `(CD ${cd})`;
        this.querySelector('.track-title').innerHTML = `${parseInt(track) + 1}. ${title} ${currentCd}`;
    }

    togglePlayPause(playing = false) {
        if (playing) {
            this.playButton.setAttribute('hidden', '');
            this.pauseButton.removeAttribute('hidden');
        } else {
            this.pauseButton.setAttribute('hidden', '');
            this.playButton.removeAttribute('hidden');
        }
    }

    enableControls() {
        for (const control of this.controls) {
            control.removeAttribute('disabled');
        }
        if (this.totalTracks === 1 && this.totalCds === 1) {
            this.nextButton.setAttribute('disabled', '');
            return;
        }
    }

    disableControls(isError = false) {
        for (const control of this.controls) {
            control.setAttribute('disabled', '');
        }
        if (isError && this.totalCds > 1 || this.totalTracks > 1) {
            this.nextButton.removeAttribute('disabled');
        }
    }

    errorHandler(error) {
        let message = '';
        const { name } = error;
        switch (name) {
            case 'NotSupportedError':
                // u.a. iOS Safari
                message = 'Der Titel kann leider nicht abgespielt werden, da das Format auf Ihrem Endgerät nicht unterstützt wird.';
                break;
            case 'EmptySourceError':
                message = 'Der Titel ist zur Zeit leider nicht verfügbar. Bitte laden Sie die Seite neu und versuchen es noch einmal.';
                break;
            case 'AbortError':
                // langlaufender fetch (timeout)
                message = 'Das Laden des Titels dauert ungewöhnlich lange. Bitte laden Sie die Seite neu und versuchen es noch einmal.';
            default:
                // sonstige Fehler: 404 etc.
                message = 'Der Titel konnte nicht geladen werden. Bitte laden Sie die Seite neu und versuchen es noch einmal.';
                break;
        }
        this.disableControls(true);
        this.toggleToastrHint(message);
    }

    toggleToastrHint(message) {
        const options = {
            mode: 'toastr',
            type: 'warning',
            text: message,
            duration: 5000
        }
        toggleHinweis(this, options);
    }

    get cd() {
        return parseInt(this.dataset['cd']);
    }

    get currentTrack() {
        return parseInt(this.dataset['currentTrack']);
    }

    set currentTrack(value) {
        this.dataset['currentTrack'] = value;
    }

    get totalCds() {
        return parseInt(this.closest('audio-player').dataset['totalCds']);
    }

    get totalTracks() {
        return parseInt(this.dataset['tracks']);
    }
    
    get isLastTrack() {
        return this.currentTrack + 1 === this.totalTracks;
    }

    get isActive() {
        return this.dataset['state'] === 'active';
    }

    get isPaused() {
        return this.audio.paused;
    }
}

if (!customElements.get("audio-custom")) {
    customElements.define("audio-custom", AudioCustom);
}
